import React from 'react';
import { IntakeFormCustomQuestion, IntakeFormCustomQuestionCategory } from '@spike/marketplace-model';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Radio } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';

interface CustomQuestionProps {
    error?: string;
    question: IntakeFormCustomQuestion;
    onChange: (question: IntakeFormCustomQuestion) => void;
    onDelete: (uuid: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            'gap': 16,
            'width': '100%',
            'borderRadius': 16,
            'display': 'flex',
            'position': 'relative',
            'flexDirection': 'column',
            'backgroundColor': '#FFFFFF',
            'border': 'solid 1px #D4D4D4',
            'padding': '16px 22px 16px 15px',

            '& *': {
                fontFamily: 'Poppins'
            }
        },
        closeButton: {
            top: 6,
            right: 9,
            padding: 4,
            position: 'absolute',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#000000',
            fontSize: 18
        },
        label: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,
            color: '#000000',
            marginBottom: 8,
            display: 'block'
        },
        labelError: {
            color: '#EF4F57'
        },
        textarea: {
            width: '100%',
            minHeight: 92,
            outline: 'none',
            resize: 'none',
            fontSize: 16,
            lineHeight: 1.3,
            fontWeight: 400,
            color: '#7A7A7A',
            borderRadius: 16,
            padding: '14px 35px',
            border: 'solid 1px #D3D3D3'
        },
        error: {
            borderColor: '#EF4F57'
        },
        radioGroup: {
            gap: 16,
            display: 'flex',
            alignItems: 'center'
        },
        radio: {
            'gap': 6,
            'margin': 0,

            '& .MuiRadio-root': {
                padding: 0
            },
            '& [class*="PrivateRadioButtonIcon"] svg': {
                width: 18
            }
        }
    })
);

export const CustomQuestion: React.FC<CustomQuestionProps> = props => {
    const classes = useStyles();

    const changeCategoryHandler = (category: IntakeFormCustomQuestionCategory) => {
        props.onChange({
            ...props.question,
            category
        });
    };

    return (
        <div className={classes.root}>
            <button
                type="button"
                className={classes.closeButton}
                onClick={() => props.onDelete(props.question.uuid)}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>

            <div>
                <label
                    className={clsx(classes.label, {
                        [classes.labelError]: props.error !== undefined
                    })}
                >
                    {props.error ? props.error : 'Question'}
                </label>

                <textarea
                    className={clsx(classes.textarea, {
                        [classes.error]: props.error !== undefined
                    })}
                    value={props.question.question}
                    onChange={e => {
                        props.onChange({
                            ...props.question,
                            question: e.target.value
                        });
                    }}
                />
            </div>

            <div className={classes.radioGroup}>
                <Radio
                    label="For Pets"
                    className={classes.radio}
                    checked={props.question.category === IntakeFormCustomQuestionCategory.FOR_PETS}
                    onChange={() => changeCategoryHandler(IntakeFormCustomQuestionCategory.FOR_PETS)}
                />
                <Radio
                    label="For Clients"
                    className={classes.radio}
                    checked={props.question.category === IntakeFormCustomQuestionCategory.FOR_CUSTOMERS}
                    onChange={() => changeCategoryHandler(IntakeFormCustomQuestionCategory.FOR_CUSTOMERS)}
                />
            </div>
        </div>
    );
};
